/**
 * AI-POWERED KIT GENERATION SERVICE
 * 
 * Uses configured AI (OpenAI) to generate intelligent kit suggestions
 * following strict product compatibility and commercial coherence rules.
 * 
 * FEATURES:
 * - AI-driven kit generation with detailed Portuguese prompt
 * - Product compatibility analysis
 * - Commercial coherence validation
 * - Proper kit naming and justification
 * - Rotation support for varied suggestions
 */

import { aiService } from './aiService';
import { productService } from './productService';

/**
 * Portuguese prompt with mandatory rules for kit generation
 */
const KIT_GENERATION_PROMPT = `REGRAS OBRIGATÓRIAS PARA GERAÇÃO DE KITS DE PRODUTOS

Você é um assistente especializado em criação de kits comerciais para e-commerce.

Seu objetivo é sugerir kits que façam sentido prático, técnico e comercial.

1. COMPATIBILIDADE ENTRE PRODUTOS

- Nunca agrupar produtos incompatíveis entre si.
- Antes de criar um kit, analise a função, uso e contexto de cada produto.
- Produtos só podem ser agrupados se forem:
  a) Complementares no uso
  b) Usados juntos no mesmo cenário
  c) Compatíveis tecnicamente

Exemplos de compatibilidade correta:
- Bola esportiva + bomba de ar
- Furadeira + brocas compatíveis
- Celular + capa + película
- Impressora + cartucho correto
- Chave Torx + parafusos Torx

Exemplos de compatibilidade incorreta:
- Produtos de marcas incompatíveis tecnicamente
- Tamanhos que não funcionam juntos
- Acessórios que não servem para o produto principal

2. ANÁLISE DO PRODUTO PRINCIPAL

- Todo kit deve ter um produto principal claramente identificado.
- Os demais itens do kit devem existir para:
  a) Complementar
  b) Facilitar
  c) Melhorar a experiência de uso do produto principal

3. NORMAS E RESTRIÇÕES

- Respeitar normas de segurança, uso e categoria do produto.
- Não criar kits que possam gerar uso indevido, perigoso ou incorreto.
- Não misturar produtos de categorias que não se relacionam.

4. COERÊNCIA COMERCIAL

- O kit deve parecer algo que um vendedor humano faria.
- Evitar kits "estranhos" ou forçados apenas para aumentar ticket.
- Priorizar kits que:
  a) Aumentem conveniência
  b) Reduzam esforço do cliente
  c) Aumentem valor percebido

5. FAIXA DE PREÇO E POSICIONAMENTO

- Os produtos do kit devem ter faixas de preço compatíveis.
- Evitar misturar produtos muito baratos com itens premium sem justificativa.
- Manter coerência de marca sempre que possível.

6. NOMENCLATURA DO KIT

- O nome do kit deve deixar claro:
  a) Para que serve
  b) Para quem é
  c) O que está incluso
- Evitar nomes genéricos ou confusos.

7. OBJETIVO FINAL

- Criar kits que façam sentido real para o cliente.
- A IA deve agir como um especialista em vendas, não como um agrupador automático.
- Se não houver produtos compatíveis suficientes, NÃO criar kit.

IMPORTANTE:
É melhor não sugerir nenhum kit do que sugerir um kit incoerente.`;

/**
 * Generate AI-powered kit suggestions from catalog products
 * @param {Array} products - Array of products from catalog
 * @param {Object} options - Generation options
 * @returns {Promise<Object>} Generated kits with AI analysis
 */
export async function generateAIKits(products, options = {}) {
  try {
    const {
      maxKits = 10,
      minProducts = 2,
      maxProducts = 5,
      seed = 0, // For rotation
      strategy = 'balanced' // NEW: Strategy parameter
    } = options;

    // Validate input
    if (!products || products?.length < minProducts) {
      return {
        success: false,
        error: 'Produtos insuficientes para gerar kits',
        kits: []
      };
    }

    // Shuffle products for rotation (different results each time)
    const shuffledProducts = [...products]?.sort(() => {
      // Use seed for deterministic but varied shuffling
      return Math.sin(seed + Math.random()) - 0.5;
    });

    // Take a subset of products for analysis (to avoid token limits)
    const productsToAnalyze = shuffledProducts?.slice(0, 50);

    // Format products for AI analysis
    const productList = productsToAnalyze?.map((p, idx) => {
      return `${idx + 1}. ${p?.name || 'Sem nome'}
   - SKU: ${p?.sku || 'N/A'}
   - Categoria: ${p?.category || 'N/A'}
   - Marca: ${p?.brand || 'N/A'}
   - Preço: R$ ${p?.price?.toFixed(2) || '0.00'}
   - Estoque: ${p?.stock || 0} unidades
   - Descrição: ${p?.description?.substring(0, 100) || 'N/A'}`;
    })?.join('\n\n');

    // Build AI prompt with strategy
    let strategyPrompt = '';
    switch (strategy) {
      case 'complementary':
        strategyPrompt = 'FOCO: Priorize produtos COMPLEMENTARES que funcionam juntos (ex: furadeira + brocas, celular + capa).';
        break;
      case 'same_category':
        strategyPrompt = 'FOCO: Priorize produtos da MESMA CATEGORIA que podem ser vendidos juntos (ex: 3 brinquedos educativos).';
        break;
      case 'high_margin':
        strategyPrompt = 'FOCO: Priorize kits com ALTA MARGEM DE LUCRO. Combine produtos com bom markup.';
        break;
      case 'quick_turnover':
        strategyPrompt = 'FOCO: Priorize produtos POPULARES com alto estoque para GIRO RÁPIDO.';
        break;
      case 'balanced':
      default:
        strategyPrompt = 'FOCO: Mix balanceado de estratégias (complementar, mesma categoria, alta margem).';
        break;
    }

    const prompt = `${KIT_GENERATION_PROMPT}

${strategyPrompt}

---

ANÁLISE OS SEGUINTES PRODUTOS E SUGIRA ATÉ ${maxKits} KITS COMERCIALMENTE VIÁVEIS:

${productList}

---

RETORNE UM JSON VÁLIDO COM A SEGUINTE ESTRUTURA:

{
  "kits": [
    {
      "name": "Nome do Kit (claro e descritivo)",
      "products": [1, 3, 5],
      "mainProduct": 1,
      "reason": "Justificativa detalhada da compatibilidade e coerência comercial",
      "targetAudience": "Para quem é este kit",
      "discount": 10
    }
  ]
}

ONDE:
- "products": array com os números dos produtos do kit (mínimo ${minProducts}, máximo ${maxProducts})
- "mainProduct": número do produto principal
- "discount": desconto sugerido em porcentagem (5-20%)
- "reason": explicação clara de por que esses produtos funcionam juntos

LEMBRE-SE: É MELHOR SUGERIR MENOS KITS DE QUALIDADE DO QUE MUITOS KITS INCOERENTES.`;

    // Call AI service
    const aiResponse = await aiService?.generateContent(prompt, {
      maxTokens: 2000,
      temperature: 0.7
    });

    // Check if AI service returned an error
    if (!aiResponse?.success) {
      console.error('AI service error:', aiResponse?.error);
      return {
        success: false,
        error: aiResponse?.error || 'Erro ao gerar kits com IA',
        kits: []
      };
    }

    // Extract content from response
    const aiContent = aiResponse?.content;

    // Parse AI response
    let parsedResponse;
    try {
      // Try to extract JSON from response
      const jsonMatch = aiContent?.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        parsedResponse = JSON.parse(jsonMatch?.[0]);
      } else {
        parsedResponse = JSON.parse(aiContent);
      }
    } catch (parseError) {
      console.error('Error parsing AI response:', parseError);
      return {
        success: false,
        error: 'Erro ao processar resposta da IA',
        kits: []
      };
    }

    // Validate and format kits
    const formattedKits = [];
    
    if (parsedResponse?.kits && Array.isArray(parsedResponse?.kits)) {
      for (const kit of parsedResponse?.kits) {
        try {
          // Get actual product objects
          const kitProducts = kit?.products
            ?.map(idx => productsToAnalyze?.[idx - 1])
            ?.filter(p => p);

          if (kitProducts?.length < minProducts) continue;

          // Calculate pricing
          const totalIndividualPrice = kitProducts?.reduce((sum, p) => sum + (p?.price || 0), 0);
          const discountPercent = kit?.discount || 10;
          const kitPrice = totalIndividualPrice * (1 - discountPercent / 100);

          formattedKits?.push({
            name: kit?.name,
            products: kitProducts,
            mainProduct: productsToAnalyze?.[kit?.mainProduct - 1],
            reason: kit?.reason,
            targetAudience: kit?.targetAudience,
            totalIndividualPrice,
            kitPrice,
            discount: discountPercent,
            savings: totalIndividualPrice - kitPrice,
            aiGenerated: true
          });
        } catch (error) {
          console.error('Error formatting kit:', error);
          continue;
        }
      }
    }

    return {
      success: true,
      kits: formattedKits,
      totalAnalyzed: productsToAnalyze?.length,
      aiProvider: 'OpenAI'
    };

  } catch (error) {
    console.error('Error generating AI kits:', error);
    return {
      success: false,
      error: error?.message || 'Erro ao gerar kits com IA',
      kits: []
    };
  }
}

/**
 * Generate kit suggestions with automatic rotation
 * @param {Object} options - Generation options
 * @returns {Promise<Object>} Generated kits
 */
export async function generateRotatingKits(options = {}) {
  try {
    // Fetch all products from catalog
    const { products } = await productService?.getAll(200);
    
    if (!products || products?.length === 0) {
      return {
        success: false,
        error: 'Nenhum produto encontrado no catálogo',
        kits: []
      };
    }

    // Generate kits with AI
    return await generateAIKits(products, options);
  } catch (error) {
    console.error('Error generating rotating kits:', error);
    return {
      success: false,
      error: error?.message,
      kits: []
    };
  }
}

export const aiKitGenerationService = {
  generateAIKits,
  generateRotatingKits
};